import { Add, Tune } from "@mui/icons-material";
import { Box, ButtonBase, Typography, alpha } from "@mui/material";
import { useState } from "react";
import { v4 } from "uuid";
import { useMoeda } from "../../../../core/hooks/useMoeda";
import { DrawerComboOperacao } from "../../../../produtos/components/DrawerComboOperacao";
import { TipoProduto } from "../../../../produtos/enum/TipoProduto";
import { IProduto } from "../../../../produtos/interfaces/IProduto";
import { IProdutoPedido } from "../../../interface/IProdutoPedido";
import { contorno, cores, gradienteMarca, limitarLinhas, raio, superficie, texto } from "../identidade";

interface ChatPdVProdutoLinhaProps {
    produto: IProduto;
    adicionarProduto: (produto: IProdutoPedido) => void;
    adicionarProdutosCombo: (produtos: IProdutoPedido[]) => void;
    onPersonalizarPizza: (produto: IProduto) => void;
    onPersonalizarPreparado: (produto: IProduto) => void;
    onPersonalizarPeso: (produto: IProduto) => void;
}

const IMAGEM_PADRAO: Partial<Record<TipoProduto, string>> = {
    [TipoProduto.pizza]: "pizza",
    [TipoProduto.preparado]: "preparado",
    [TipoProduto.industrializado]: "industrializado",
    [TipoProduto.combo]: "combo",
};

/**
 * Um produto do catálogo, em linha.
 *
 * O chat divide a largura com a conversa, então o catálogo aqui não cabe em
 * cards lado a lado: nessa largura eles precisavam cortar o nome em ~25
 * caracteres para caber. Em linha, o nome inteiro tem a largura da coluna e
 * pode ocupar duas linhas — "Sanduíche de pernil com queijo prato e vinagrete"
 * aparece por extenso.
 *
 * O comportamento por tipo de produto é o mesmo dos cards do PDV: pizza e peso
 * sempre abrem o formulário, preparado abre só quando tem complemento ou
 * observação, industrializado entra direto e combo abre o drawer de montagem.
 *
 * A linha é um `<button>`: o operador anda de produto em produto com TAB e
 * escolhe com ENTER, sem tocar no mouse. O `ButtonBase` do MUI zera o
 * `outline`, então o estado de foco é desenhado aqui — era o realce do
 * `CardActionArea` dos cards antigos que dizia ao operador onde ele está, e
 * sem ele o TAB anda às cegas.
 */
export const ChatPdVProdutoLinha = ({
    produto,
    adicionarProduto,
    adicionarProdutosCombo,
    onPersonalizarPizza,
    onPersonalizarPreparado,
    onPersonalizarPeso,
}: ChatPdVProdutoLinhaProps) => {
    const { real } = useMoeda();
    const [drawerComboAberto, setDrawerComboAberto] = useState(false);

    const precisaPersonalizarPreparado = !!(
        produto.configuracao?.habilitarComplementos || produto.configuracao?.habilitarObservacao
    );

    /** Abre formulário/drawer em vez de somar direto no carrinho. */
    const abreEscolha =
        produto.tipo === TipoProduto.pizza ||
        produto.tipo === TipoProduto.peso ||
        produto.tipo === TipoProduto.combo ||
        (produto.tipo === TipoProduto.preparado && precisaPersonalizarPreparado);

    const acionar = () => {
        if (produto.tipo === TipoProduto.pizza) {
            return onPersonalizarPizza(produto);
        }

        if (produto.tipo === TipoProduto.peso) {
            return onPersonalizarPeso(produto);
        }

        if (produto.tipo === TipoProduto.combo) {
            return setDrawerComboAberto(true);
        }

        if (produto.tipo === TipoProduto.preparado) {
            if (precisaPersonalizarPreparado) {
                return onPersonalizarPreparado(produto);
            }

            return adicionarProduto({
                ...produto,
                tipo: TipoProduto.preparado,
                uuid: v4(),
                uuidProdutoReferencia: produto.uuid,
            });
        }

        return adicionarProduto({ ...produto, uuidProdutoReferencia: produto.uuid });
    };

    const imagem =
        produto.imagens?.[0]?.url ??
        `${process.env.PUBLIC_URL}/images/produtos/${IMAGEM_PADRAO[produto.tipo] ?? "generico"}.png`;

    return (
        <>
            <ButtonBase
                onClick={acionar}
                sx={{
                    width: "100%",
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "flex-start",
                    textAlign: "left",
                    gap: 1.5,
                    px: 1.5,
                    py: 1.5,
                    borderRadius: raio.md,
                    border: theme => contorno(theme, 0.07),
                    backgroundColor: theme => superficie(theme, 0.025),
                    scrollMargin: "8px",
                    transition: theme => theme.transitions.create(["background-color", "border-color", "box-shadow"], { duration: 160 }),
                    "&:hover": {
                        backgroundColor: theme => superficie(theme, 0.055),
                        borderColor: alpha(cores.marca[500], 0.35),
                    },
                    "&.Mui-focusVisible, &:focus-visible": {
                        backgroundColor: alpha(cores.marca[500], 0.1),
                        borderColor: cores.marca[500],
                        boxShadow: `0 0 0 2px ${alpha(cores.marca[500], 0.3)}`,
                    },
                }}
            >
                <Box
                    component="img"
                    src={imagem}
                    alt={produto.descricao}
                    sx={{
                        width: 52,
                        height: 52,
                        flexShrink: 0,
                        borderRadius: raio.sm,
                        objectFit: "cover",
                        border: theme => contorno(theme, 0.07),
                        backgroundColor: theme => superficie(theme, 0.04),
                    }}
                />

                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ ...texto.titulo, lineHeight: 1.35, ...limitarLinhas(2) }}>
                        {produto.descricao}
                    </Typography>

                    {!!produto.detalhes && (
                        <Typography
                            sx={{ ...texto.apoio, color: "text.secondary", mt: 0.35, ...limitarLinhas(2) }}
                        >
                            {produto.detalhes}
                        </Typography>
                    )}

                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.75, flexWrap: "wrap" }}>
                        <Typography sx={{ fontSize: "13px", fontWeight: 800, color: cores.marca[500] }}>
                            {real(produto.preco ?? 0)}
                        </Typography>
                        {abreEscolha && (
                            <Box
                                sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 0.4,
                                    px: 0.75,
                                    py: 0.25,
                                    borderRadius: raio.pill,
                                    backgroundColor: theme => superficie(theme, 0.06),
                                    color: "text.secondary",
                                }}
                            >
                                <Tune sx={{ fontSize: 11 }} />
                                <Typography sx={{ ...texto.etiqueta, fontWeight: 600 }}>
                                    {produto.tipo === TipoProduto.combo ? "Montar" : "Escolher"}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>

                <Box
                    sx={{
                        flexShrink: 0,
                        width: 32,
                        height: 32,
                        borderRadius: raio.sm,
                        display: "grid",
                        placeItems: "center",
                        color: "#fff",
                        background: gradienteMarca,
                    }}
                >
                    {abreEscolha ? <Tune sx={{ fontSize: 16 }} /> : <Add sx={{ fontSize: 18 }} />}
                </Box>
            </ButtonBase>

            {produto.tipo === TipoProduto.combo && (
                <DrawerComboOperacao
                    open={drawerComboAberto}
                    toggleDrawer={() => setDrawerComboAberto(false)}
                    produto={produto}
                    adicionarProdutosCombo={adicionarProdutosCombo}
                />
            )}
        </>
    );
};
